import { createAsyncSelectors } from '@lib/selectors';
import { getMax } from '@sonar-software/sonar-ts-lib';
import { useInitiativeReference } from './useInitiativeReference';
import { coreActions, PostInitiativeTaskResponse, Task } from '@sonar-software/sonar-core';
import { useAction, useSonarSelector } from '@hooks';
import { useCallback, useMemo } from 'react';

function getNextOrderIndex(tasks: Array<Task>): number {
	return (getMax(tasks.map((task) => task.orderIndex)) ?? -1) + 1;
}

export type CreateTaskOptions = {
	assignedTo?: number;
	name: string;
};

export interface UseCreateInitiativeTaskResult {
	isProcessing: boolean;
	isSuccessful: boolean;
	request: (data: CreateTaskOptions) => void;
	reset: () => void;
	result: PostInitiativeTaskResponse | null | undefined;
}

const selectors = createAsyncSelectors((state) => state.core.initiatives.tasks.post);

export function useCreateInitiativeTask(initiativeId: number) {
	const { tasks } = useInitiativeReference(initiativeId);

	const requestBase = useAction(coreActions.requestPostCoreInitiativesTasks);
	const reset = useAction(coreActions.resetPostCoreInitiativesTasks);

	const isProcessing = useSonarSelector(selectors.isRequested);
	const isSuccessful = useSonarSelector(selectors.isReceived);
	const result = useSonarSelector(selectors.data);

	const nextOrderIndex = useMemo(() => getNextOrderIndex(tasks), [ tasks ]);

	const request = useCallback(
		(data: CreateTaskOptions) => {
			if (initiativeId) {
				requestBase({
					assignedOn: '',
					assignedTo: data.assignedTo ?? 0,
					completed: false,
					completedBy: 0,
					completedOn: '',
					initiativeId,
					name: data.name,
					orderIndex: nextOrderIndex
				});
			}
		},
		[ initiativeId, nextOrderIndex, requestBase ]
	);

	return {
		isProcessing,
		isSuccessful,
		request,
		reset,
		result
	};
}

useCreateInitiativeTask.selectors = selectors;
